import { observable, action } from "mobx";

import Model from "./Model";
import User from "./User";
import Cycle from "../store/Cycle";

class Scenario extends Model {
  static basePath = "/api/scenarios";
  static verboseName = "Scenario";

  static CREATED = "created";
  static QUEUED = "queued";
  static STARTED = "started";
  static SUCCESS = "success";
  static WARNING = "warning";
  static FAIL = "fail";

  @observable type;
  @observable cycle_year;
  @observable cycle_month;
  @observable name;
  @observable description;
  @observable parent;
  @observable factories;
  @observable technologies;

  @observable apo_attributes_version;
  @observable apo_bom_wrc_version;
  @observable bom_version;
  @observable bpc_hierarchy_mapping_version;
  @observable cbus_hierarchies_version;
  @observable conversion_rates_version;
  @observable demand_forecasts_version;
  @observable demand_actuals_version;
  @observable demand_rf_gps_version;
  @observable dummy_skus_version;
  @observable demand_risks_opps_version;
  @observable factory_names_version;
  @observable fg_capacity_version;
  @observable fg_release_rates_version;
  @observable fg_run_rates_version;
  @observable fg_init_allocation_version;
  @observable fg_transfer_rules_version;
  @observable fx_rates_version;
  @observable hierarchy_stage_mapping_version;
  @observable innovations_version;
  @observable line_feasibility_fg_version;
  @observable line_feasibility_sfg_version;
  @observable line_techno_scope_version;
  @observable location_mapping_version;
  @observable min_availability_version;
  @observable manual_bom_version;
  @observable moq_version;
  @observable oe_ou_actuals_version;
  @observable out_of_scope_sfg_dem_version;
  @observable planned_stock_version;
  @observable planned_transfers_version;
  @observable plants_hierarchies_version;
  @observable prices_version;
  @observable product_hierarchy_version;
  @observable production_plan_version;
  @observable rp_alerts_version;
  @observable rp_purchasing_actuals_version;
  @observable sap_production_plan_version;
  @observable scope_supply_point_version;
  @observable seasonal_bom_version;
  @observable sfg_capacity_version;
  @observable sfg_consumption_actuals_version;
  @observable sfg_release_rates_version;
  @observable sfg_run_rates_version;
  @observable sourcing_sfg_version;
  @observable standard_cost_prices_version;
  @observable stock_version;
  @observable stock_target_version;
  @observable third_parties_sourcing_version;
  @observable transports_version;
  @observable transports_leadtimes_version;

  @observable version_changes;

  @observable output_file;
  @observable df_raws_file;
  @observable df_masterdata_file;
  @observable demand_table_file;
  @observable actuals_supply_file;
  @observable result_sfg_file;
  @observable result_fg_file;
  @observable moves_df_file;
  @observable costs_df_file;
  @observable df_issues_file;

  @observable prediction_duration;
  @observable target_unit_fg;
  @observable target_unit_sfg;
  @observable target_currency;
  @observable load_unit_demand;
  @observable load_unit_capacity;
  @observable load_unit_price;
  @observable load_unit_price_inno;
  @observable load_unit_costs_inno;
  @observable load_unit_risks_opps;
  @observable load_unit_innovations;
  @observable load_unit_out_of_scope;
  @observable load_unit_apo_fg;
  @observable load_unit_apo_sfg;
  @observable optimization_mip_gap;
  @observable optimization_month_start;
  @observable optimization_constraints_move_definition;
  @observable optimization_above_max_alpha;
  @observable optimization_below_target_alpha;
  @observable optimization_above_target_alpha;
  @observable optimization_margin_alpha;
  @observable remove_out_of_scope_capacity;
  @observable optimization_add_innovations;
  @observable optimization_constraints_cash_constraints;
  @observable optimization_constraints_min_prod;
  @observable optimization_constraints_min_order;
  @observable optimization_constraints_lead_times;
  @observable optimization_constraints_third_parties;
  @observable optimization_constraints_min_month_move;
  @observable optimization_constraints_max_month_move;
  @observable optimization_constraints_techno_list_move;
  @observable optimization_constraints_plant_list_move;
  @observable optimization_moves_capacity;
  @observable optimization_move_capacity_step;
  @observable optimization_max_fg_transfers;
  @observable optimization_max_sfg_transfers;
  @observable optimization_neg_stocks_not_to_recover;

  @observable status;
  @observable created_by;
  @observable created_at;
  @observable modified_at;
  @observable steps;
  @observable git_commit;

  // Stored methods
  @observable sales;
  @observable costs;
  @observable availability_fg;
  @observable capacity_sfg;
  @observable capacity_fg;
  @observable stock_sfg;
  @observable stock_fg;
  @observable cash_locked;
  @observable fg_prod_demand;
  @observable sfg_prod_demand;
  @observable prod_demand;
  @observable required_actions;
  @observable stock_nb_total;
  @observable stock_vol_total;
  @observable stock_nb_alerts_below;
  @observable stock_vol_alerts_below;
  @observable stock_nb_alerts_above;
  @observable stock_vol_alerts_above;

  @observable sales_kpi;
  @observable costs_kpi;
  @observable cash_locked_kpi;
  @observable fg_prod_demand_kpi;
  @observable sfg_prod_demand_kpi;
  @observable prod_demand_kpi;
  @observable availability_fg_kpi;
  @observable capacity_sfg_kpi;
  @observable capacity_fg_kpi;
  @observable stock_sfg_kpi;
  @observable stock_fg_kpi;
  @observable active_vs_available_capacity_sfg_kpi;
  @observable active_vs_available_capacity_fg_kpi;

  static get fieldsData() {
    const fieldsData = super.fieldsData;
    if (fieldsData !== null && typeof fieldsData.steps.initial === "string") {
      fieldsData.steps = {
        ...fieldsData.steps,
        initial: JSON.parse(fieldsData.steps.initial),
      };
    }
    return fieldsData;
  }

  get cycle() {
    if (!this.isInitialized) {
      return null;
    }
    return new Cycle(this.cycle_year, this.cycle_month);
  }

  get cycleStr() {
    if (!this.isInitialized) {
      return null;
    }
    return this.cycle.toString();
  }

  get listUrl() {
    return "/scenarios/";
  }

  get instanceBaseUrl() {
    return `${this.listUrl}${this.id}`;
  }

  get detailUrl() {
    return `${this.instanceBaseUrl}/detail`;
  }

  get runReportUrl() {
    return `${this.instanceBaseUrl}/run-report`;
  }

  get editUrl() {
    return `${this.instanceBaseUrl}/edit`;
  }

  get copyUrl() {
    return `${this.instanceBaseUrl}/copy`;
  }

  get isBase() {
    return this.type === 1 || this.type === "1";
  }

  get isSimulation() {
    return this.type === 3 || this.type === "3";
  }

  get isReducedScopeSimulation() {
    return this.type === 4 || this.type === "4";
  }

  get isValidated() {
    return this.type === 0 || this.type === "0";
  }

  get isBaseOptimization() {
    return this.type === 2 || this.type === "2";
  }

  get isUnconstrainedCapacity() {
    return this.type === 5 || this.type === "5";
  }

  get isDraft() {
    return this.type === 6 || this.type === "6";
  }

  @action async copyInstance() {
    const copy = await super.copyInstance();
    if (!(copy.isSimulation || copy.isReducedScopeSimulation)) {
      copy.type = 3;
    }
    copy.name = "";
    return copy;
  }

  get isQueued() {
    return this.status === this.constructor.QUEUED;
  }

  get isStarted() {
    return this.status === this.constructor.STARTED;
  }

  get isSuccess() {
    return this.status === this.constructor.SUCCESS;
  }

  get isFail() {
    return this.status === this.constructor.FAIL;
  }

  get createdByUser() {
    if (this.created_by) {
      return User.getForId(this.created_by);
    }
    return null;
  }

  toString() {
    if (this.name) {
      return this.name;
    }
    return super.toString();
  }

  runModel() {
    return this.ajaxRequest("post", "/run_model/");
  }

  get versions() {
    return Object.assign(
      {},
      ...Object.entries(this)
        .filter(([key, value]) => key.endsWith("_version"))
        .map(([key, value]) => ({ [key]: value }))
    );
  }

  get stocks_kpi() {
    if (this.stock_fg_kpi && this.stock_sfg_kpi) {
      return {
        value: this.stock_fg_kpi.value + this.stock_sfg_kpi.value,
        unit: this.stock_fg_kpi.unit,
      };
    }
    return null;
  }
}

export default Scenario;
